import type { AuditRow } from "./types";
import "./AuditLogTable.css";

interface AuditLogTableProps {
  rows: AuditRow[];
  /** Show the Organization column (admin/cross-tenant view). */
  showOrgColumn?: boolean;
  loading?: boolean;
  emptyMessage?: string;
  /** Load-more button — supplied when there's more to page through. */
  onLoadMore?: () => void;
  loadingMore?: boolean;
  hasMore?: boolean;
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleString();
}

/**
 * The Resource column shows the type (page, user, document, …); this fills
 * the Details column with the most-useful field from metadata so page.view
 * rows show the actual path, user events show the email, etc.
 *
 * Reads are string-typed so a malformed row can't crash the render.
 */
function describeAudit(row: AuditRow): string {
  const m = (row.metadata ?? {}) as Record<string, unknown>;
  const s = (v: unknown): string | null =>
    typeof v === "string" && v.length > 0 ? v : null;

  // page.view (both client- and middleware-authored rows carry metadata.path)
  const path = s(m.path);
  if (path) return path;

  const email = s(m.email);
  if (email) return email;

  const filename = s(m.filename) || s(m.original_name) || s(m.name);
  if (filename) return filename;

  if (row.resource_id) {
    return `${row.resource_type ?? "resource"}:${row.resource_id.slice(0, 8)}`;
  }
  return "—";
}

export function AuditLogTable({
  rows,
  showOrgColumn = false,
  loading = false,
  emptyMessage = "No activity recorded yet.",
  onLoadMore,
  loadingMore = false,
  hasMore = false,
}: AuditLogTableProps) {
  if (loading && rows.length === 0) {
    return <div className="audit-table-status">Loading activity...</div>;
  }

  if (rows.length === 0) {
    return <div className="audit-table-status audit-table-empty">{emptyMessage}</div>;
  }

  return (
    <div className="audit-table-wrapper">
      <table className="audit-table">
        <thead>
          <tr>
            <th className="audit-table-time">Time</th>
            <th>User</th>
            {showOrgColumn && <th>Organization</th>}
            <th>Action</th>
            <th>Resource</th>
            <th>Details</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const detail = describeAudit(row);
            return (
              <tr key={row.id}>
                <td className="audit-table-time" title={row.created_at}>
                  {formatTime(row.created_at)}
                </td>
                <td>{row.user_name || row.user_email || "System"}</td>
                {showOrgColumn && (
                  <td>{row.org_name || "—"}</td>
                )}
                <td>
                  <code className="audit-table-action">{row.action}</code>
                </td>
                <td className="audit-table-resource">
                  {row.resource_type || "—"}
                </td>
                <td className="audit-table-detail" title={detail}>
                  {detail}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {onLoadMore && (
        <div className="audit-table-load-more">
          {hasMore ? (
            <button
              type="button"
              onClick={onLoadMore}
              disabled={loadingMore}
              className="audit-table-load-more-btn"
            >
              {loadingMore ? "Loading..." : "Load more"}
            </button>
          ) : (
            <span className="audit-table-end">End of results</span>
          )}
        </div>
      )}
    </div>
  );
}
